import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { DeliveryAnalyticsFilters } from 'src/types/TDeliveryAnalytics';

export type RangeKey = '24h' | '7d' | '30d' | '90d';

export const RANGE_HOURS: Record<RangeKey, number> = {
  '24h': 24,
  '7d': 24 * 7,
  '30d': 24 * 30,
  '90d': 24 * 90,
};

export const RANGE_LABELS: Record<RangeKey, string> = {
  '24h': 'Last 24 hours',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
};

/** Campaign/channel-attribution filter keys (canonical derived dimensions).
 *  Reflected in the URL so a filtered view is shareable / survives reload. */
const ATTR_KEYS = [
  'campaign_key',
  'channel_key',
  'placement_key',
  'variant_key',
  'audience_key',
] as const;
type AttrKey = (typeof ATTR_KEYS)[number];

export interface DeliveryFiltersState {
  /** Active preset range. */
  range: RangeKey;
  /** Campaign attribution values (empty string = unset). */
  attrs: Record<AttrKey, string>;
  /** Ready-to-send query filters (range expanded to from/to RFC3339). */
  queryFilters: DeliveryAnalyticsFilters;
  setRange: (r: RangeKey) => void;
  setAttr: (key: AttrKey, value: string) => void;
  /** Commit all four attr values in a single URL update (used by the Apply button). */
  setAttrs: (values: Record<AttrKey, string>) => void;
  clearAttrs: () => void;
}

function isRangeKey(v: string | null): v is RangeKey {
  return v === '24h' || v === '7d' || v === '30d' || v === '90d';
}

/**
 * Delivery-analytics filter state, persisted in the URL query string so the
 * 5 tab panels share one filter context and views are shareable. The active
 * tab itself lives in the URL too (`tab=`) — managed by the page component.
 */
export function useDeliveryFilters(): DeliveryFiltersState {
  const history = useHistory();
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const rangeParam = params.get('range');
  const range: RangeKey = isRangeKey(rangeParam) ? rangeParam : '7d';

  const attrs = useMemo(() => {
    const out = {} as Record<AttrKey, string>;
    ATTR_KEYS.forEach((k) => {
      out[k] = params.get(k) ?? '';
    });
    return out;
  }, [params]);

  const queryFilters = useMemo<DeliveryAnalyticsFilters>(() => {
    const now = new Date();
    const from = new Date(now.getTime() - RANGE_HOURS[range] * 3600_000);
    return {
      from: from.toISOString(),
      to: now.toISOString(),
      campaignKey: attrs.campaign_key || undefined,
      channelKey: attrs.channel_key || undefined,
      placementKey: attrs.placement_key || undefined,
      variantKey: attrs.variant_key || undefined,
      audienceKey: attrs.audience_key || undefined,
    };
    // `now` recomputes on every render by design — react-query keys off the
    // attrs/range, not the exact millisecond, because we round via staleTime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    range,
    attrs.campaign_key,
    attrs.channel_key,
    attrs.placement_key,
    attrs.variant_key,
    attrs.audience_key,
  ]);

  const replaceParams = useCallback(
    (mutate: (p: URLSearchParams) => void) => {
      const next = new URLSearchParams(location.search);
      mutate(next);
      history.replace({ pathname: location.pathname, search: next.toString() });
    },
    [history, location.pathname, location.search],
  );

  const setRange = useCallback(
    (r: RangeKey) => replaceParams((p) => p.set('range', r)),
    [replaceParams],
  );

  const setAttr = useCallback(
    (key: AttrKey, value: string) =>
      replaceParams((p) => {
        if (value) p.set(key, value);
        else p.delete(key);
      }),
    [replaceParams],
  );

  const setAttrs = useCallback(
    (values: Record<AttrKey, string>) =>
      replaceParams((p) => {
        ATTR_KEYS.forEach((k) => {
          const v = (values[k] || '').trim();
          if (v) p.set(k, v);
          else p.delete(k);
        });
      }),
    [replaceParams],
  );

  const clearAttrs = useCallback(
    () => replaceParams((p) => ATTR_KEYS.forEach((k) => p.delete(k))),
    [replaceParams],
  );

  return { range, attrs, queryFilters, setRange, setAttr, setAttrs, clearAttrs };
}
